

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../../utils/api';
import BottomNav from '../../../components/layout/BottomNav';
import BackButton from '../../../components/common/BackButton';

const G = '#1A6B3C';
const LIGHT = '#EAF3DE';

const SPEC_ICONS = {
  vegetables:'', flowers:'', seedlings:'', fruits:'',
  herbs:'', indoor:'', pots:'', other:'', organic:'',
};

// Category keys used on the Nursery home page ("Shop by category") mapped
// to the specialty tag values / labels used on nursery records, so tapping
// a category chip there filters this list to matching nurseries.
const CATEGORY_LABELS = {
  vegetables: 'Vegetables',
  flowers:    'Flowering',
  fruits:     'Fruits',
  herbs:      'Herbs',
  indoor:     'Indoor',
  pots:       'Pots',
  seeds:      'Seeds',
  tools:      'Tools',
};

// Subcategories shown under each category (e.g. Vegetables → Tomato, Radish,
// Beans). Display-only for now — tapping one highlights it and narrows the
// search box so matching nurseries surface, without needing a separate
// plant-level API call.
const SUBCATEGORIES = {
  vegetables: ['Tomato', 'Brinjal', 'Chilli', 'Okra', 'Beans', 'Radish', 'Onion', 'Potato', 'Cabbage', 'Cauliflower', 'Carrot', 'Spinach'],
  flowers:    ['Rose', 'Marigold', 'Jasmine', 'Hibiscus', 'Sunflower', 'Lily', 'Chrysanthemum'],
  fruits:     ['Mango', 'Banana', 'Papaya', 'Guava', 'Lemon', 'Pomegranate', 'Grapes'],
  herbs:      ['Mint', 'Basil', 'Curry Leaf', 'Coriander', 'Aloe Vera', 'Tulsi'],
  indoor:     ['Money Plant', 'Snake Plant', 'Peace Lily', 'ZZ Plant', 'Areca Palm'],
  pots:       ['Ceramic Pot', 'Plastic Pot', 'Hanging Pot', 'Terracotta Pot'],
  seeds:      ['Vegetable Seeds', 'Flower Seeds', 'Herb Seeds', 'Fruit Seeds'],
  tools:      ['Trowel', 'Pruner', 'Watering Can', 'Gloves', 'Shovel'],
};

export default function NurseryListPage() {
  const navigate  = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [nurseries, setNurseries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState(searchParams.get('search') || '');
  const [filter,    setFilter]    = useState('All');
  const [activeSub, setActiveSub] = useState('');

  const specialty = searchParams.get('specialty') || '';
  const subOptions = SUBCATEGORIES[specialty.toLowerCase()] || [];

  // Display/selection only for now — highlights the chosen subcategory
  // without narrowing the nursery list, since nurseries aren't tagged at
  // the individual plant level yet.
  const toggleSub = (sub) => setActiveSub(prev => (prev === sub ? '' : sub));

  useEffect(() => { loadNurseries(); }, []);

  const loadNurseries = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/nursery?limit=50&isActive=true');
      setNurseries(data.nurseries || data.data || []);
    } catch { setNurseries([]); }
    finally { setLoading(false); }
  };

  const FILTERS = ['All', 'Verified', 'Nearby'];

  const clearSpecialty = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('specialty');
    setSearchParams(next);
  };

  const filtered = nurseries.filter(n => {
    const matchSearch = !search ||
      n.name?.toLowerCase().includes(search.toLowerCase()) ||
      n.city?.toLowerCase().includes(search.toLowerCase()) ||
      n.ownerName?.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === 'All' ? true :
      filter === 'Verified' ? n.isVerified :
      true;
    const matchSpecialty = !specialty ||
      (n.specialties || n.categories || []).some(s =>
        s?.toLowerCase() === specialty.toLowerCase() ||
        s?.toLowerCase() === (CATEGORY_LABELS[specialty.toLowerCase()] || '').toLowerCase()
      );
    return matchSearch && matchFilter && matchSpecialty;
  });

  return (
    <div style={{ background:'#F5F7FA', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif', paddingBottom:80 }}>

      {/* Header */}
      <div style={{ background:G, padding:'14px 16px 14px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <BackButton />
          <div style={{ fontSize:17, fontWeight:700, color:'white' }}>All Nurseries</div>
          <div style={{ width:34, height:34, borderRadius:'50%', background:'rgba(255,255,255,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:'white', fontWeight:700 }}>
            {filtered.length}
          </div>
        </div>

        <style>{`
  .search-input::placeholder {
    color: rgba(255,255,255,0.8);
    opacity: 1;
  }

  .search-input::-webkit-input-placeholder {
    color: rgba(255,255,255,0.8);
  }

  .search-input::-moz-placeholder {
    color: rgba(255,255,255,0.8);
  }
`}</style>

        {/* Search */}
        <div style={{ background:'rgba(255,255,255,.15)', borderRadius:25, padding:'5px 16px', display:'flex', alignItems:'center', gap:10, border:'1px solid rgba(255,255,255,.25)' }}>
          <span style={{ fontSize:16 }}>🔍</span>
          <input
           className="search-input"
//               type="text"
          value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or city..."
            style={{ border:'none', background:'transparent', color:'white', fontSize:13, flex:1, outline:'none' }} />
          {search && <button onClick={() => setSearch('')} style={{ background:'none', border:'none', color:'rgba(255,255,255,.7)', fontSize:18, cursor:'pointer', padding:0 }}>×</button>}
        </div>
      </div>

      {/* Filter pills */}
      {/* <div style={{ display:'flex', gap:8, padding:'12px 14px 0', overflowX:'auto', scrollbarWidth:'none' }}>
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ flexShrink:0, padding:'7px 16px', borderRadius:20, border:`1.5px solid ${filter===f?G:'#E5E7EB'}`, background:filter===f?G:'white', color:filter===f?'white':'#6B7280', fontSize:12, fontWeight:filter===f?700:500, cursor:'pointer' }}>
            {f}
          </button>
        ))}
      </div> */}

      {/* Active category filter (from Home page "Shop by category") */}
      {specialty && (
        <div style={{ padding:'10px 14px 0' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:LIGHT, color:G, border:'1px solid #BBF7D0', borderRadius:20, padding:'6px 8px 6px 14px', fontSize:12, fontWeight:700 }}>
            {SPEC_ICONS[specialty.toLowerCase()] && <span>{SPEC_ICONS[specialty.toLowerCase()]}</span>}
            Category: {CATEGORY_LABELS[specialty.toLowerCase()] || specialty}
            <button onClick={clearSpecialty}
              style={{ width:20, height:20, borderRadius:'50%', border:'none', background:'white', color:G, fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              ×
            </button>
          </div>
        </div>
      )}

      {/* Subcategories within the selected category (e.g. Vegetables → Tomato, Radish, Beans) */}
      {specialty && subOptions.length > 0 && (
        <div style={{ display:'flex', gap:8, padding:'10px 14px 0', overflowX:'auto', scrollbarWidth:'none' }}>
          {subOptions.map(sub => (
            <button key={sub} onClick={() => toggleSub(sub)}
              style={{
                flexShrink:0, padding:'6px 14px', borderRadius:16,
                border:`1px solid ${activeSub===sub?G:'#E5E7EB'}`,
                background:activeSub===sub?G:'white',
                color:activeSub===sub?'white':'#4B5563',
                fontSize:11.5, fontWeight:activeSub===sub?700:500, cursor:'pointer',
              }}>
              {sub}
            </button>
          ))}
        </div>
      )}

      <div style={{ padding:'12px 14px 0' }}>
        {loading ? (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {[1,2,3].map(i => <div key={i} style={{ height:200, background:'white', borderRadius:16, opacity:0.5 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:'50px 0', color:'#9CA3AF' }}>
            <div style={{ fontSize:48, marginBottom:10 }}></div>
            <div style={{ fontSize:14 }}>
              {nurseries.length === 0
                ? 'No nurseries found'
                : specialty
                  ? `No nurseries found for "${CATEGORY_LABELS[specialty.toLowerCase()] || specialty}"`
                  : 'No results'}
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {filtered.map(n => (
              <div key={n._id} style={{ background:'white', borderRadius:16, overflow:'hidden', border:'0.5px solid #E5E7EB', boxShadow:'0 2px 8px rgba(0,0,0,.05)' }}>

                {/* Cover photo */}
                <div style={{ height:230, background:LIGHT, position:'relative', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {n.coverPhoto || n.photo ? (
                    <img src={n.coverPhoto || n.photo} alt={n.name || 'Nursery'}
                      style={{ width:'120%', height:'150%', objectFit:'cover', objectPosition:'center 35%', display:'block' }} />
                  ) : (
                    <span style={{ fontSize:64 }}></span>
                  )}
                  {n.isVerified && (
                    <div style={{ position:'absolute', top:10, right:10, background:G, color:'white', fontSize:10, fontWeight:700, padding:'4px 10px', borderRadius:20, display:'flex', alignItems:'center', gap:4 }}>
                      ✓ Verified
                    </div>
                  )}
                </div>

                {/* Info */}
                <div style={{ padding:'12px 14px' }}>

                  {/* Name + distance */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, minHeight:24 }}>
                    <div style={{ fontSize:20, fontWeight:800, color:'#1a1a1a' }}>{n.name || 'Unnamed Nursery'}</div>
                    <div style={{ fontSize:13, color:'#EF4444', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
                       {n.distance != null ? `${n.distance} km` : '—'}
                    </div>
                  </div>

                  {/* Owner / Mobile / Location / Time — 4-up icon grid, always shown for consistent layout */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(0, 1fr))', gap:6, marginBottom:12 }}>
                    <InfoItem icon="👤" label="Owner" value={n.ownerName || '—'} />
                    <InfoItem icon="📞" label="Mobile" value={n.mobile || '—'} />
                    <InfoItem icon="" label="Location" value={n.city || n.address?.split(',')[0] || '—'} />
                    <InfoItem icon="🕐" label="Time" value={n.timings || '—'} />
                  </div>

                  {/* Specialties */}
                  {n.specialties?.length > 0 && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                      {n.specialties.map(s => (
                        <span key={s} style={{ background:'#EAF3DE', color:G, border:'1px solid #BBF7D0', fontSize:10, fontWeight:600, padding:'3px 10px', borderRadius:20, textTransform:'capitalize', display:'flex', alignItems:'center', gap:4 }}>
                          {SPEC_ICONS[s.toLowerCase()] && <span>{SPEC_ICONS[s.toLowerCase()]}</span>} {s}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Footer: status + views (left) + View Plants (right) */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:10, borderTop:'1px solid #F1F3EE' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                      <span style={{ fontSize:12, color:'#22C55E', fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
                        <span style={{ width:7, height:7, borderRadius:'50%', background:'#22C55E', display:'inline-block' }} /> Active
                      </span>
                      <span style={{ fontSize:12, color:'#9CA3AF', display:'flex', alignItems:'center', gap:4 }}>
                        👁 {n.views ?? 0} Views
                      </span>
                    </div>
                    <button onClick={() => navigate(`/nursery/${n._id}`)}
                      style={{ background:G, color:'white', border:'none', borderRadius:12, padding:'11px 20px', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, boxShadow:'0 3px 10px rgba(26,107,60,.3)' }}>
                      View Plants →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:4, minWidth:0, width:'100%' }}>
      <div style={{ width:28, height:28, borderRadius:'50%', background:LIGHT, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, flexShrink:0 }}>
        {icon}
      </div>
      <div style={{ fontSize:9.5, color:'#9CA3AF', fontWeight:600 }}>{label}</div>
      <div
        title={value}
        style={{
          fontSize:10.5, color:'#1a1a1a', fontWeight:700,
          width:'100%', maxWidth:'100%',
          overflow:'hidden', textOverflow:'ellipsis',
          display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical',
          wordBreak:'break-word', lineHeight:1.25,
        }}>
        {value}
      </div>
    </div>
  );
}